import { z } from "zod";
import { IdSchema, IsoDateTimeSchema, Sha256Schema } from "./primitives.js";
import { DraftReviewDispositionSchema, RolloutValidationSuiteSchema, } from "./rollout-governance.js";
export const M4LatencyMetricNameSchema = z.enum([
    "patient_header_and_encounter_shell",
    "first_ai_action_card",
    "common_fast_path_care_bundle_complete",
    "deep_path_bundle_complete",
    "deterministic_bundle_validation",
    "review_surface_render_after_data_ready",
    "approved_action_durable_local_acceptance",
]);
export const M4LatencyEvidenceSourceSchema = z.enum([
    "throttled_browser_wall_clock",
    "controlled_synthetic_trace",
]);
export const M4LatencyEvidenceSchema = z
    .object({
    metric: M4LatencyMetricNameSchema,
    source: M4LatencyEvidenceSourceSchema,
    sampleCount: z.number().int().min(5),
    observedP50Ms: z.number().nonnegative(),
    observedP95Ms: z.number().nonnegative(),
    observedP99Ms: z.number().nonnegative(),
    targetP95Ms: z.number().positive(),
    targetP99Ms: z.number().positive(),
})
    .strict()
    .superRefine((metric, context) => {
    if (metric.observedP50Ms > metric.observedP95Ms ||
        metric.observedP95Ms > metric.observedP99Ms) {
        context.addIssue({
            code: "custom",
            path: ["observedP95Ms"],
            message: "Latency percentiles must be monotonically nondecreasing",
        });
    }
});
export const M4ClinicHardwareProfileSchema = z
    .object({
    profileId: z.literal("clinic-baseline-v1"),
    logicalCpuCores: z.literal(4),
    memoryGb: z.literal(8),
    viewportWidthPx: z.literal(1366),
    viewportHeightPx: z.literal(768),
    cpuSlowdownRate: z.literal(4),
    networkLatencyMs: z.literal(20),
    downloadMbps: z.literal(20),
    uploadMbps: z.literal(5),
    reducedMotion: z.literal(true),
    browserEngine: z.literal("chromium"),
    browserVersion: z.string().min(1).max(100),
    hostOperatingSystem: z.string().min(1).max(200),
})
    .strict();
export const M4ReviewObservationSchema = z
    .object({
    case: z.enum(["complete_fast_path", "complex_physician_deep_path"]),
    actionDispositions: z.array(DraftReviewDispositionSchema).min(1).max(50),
    manualBaselineSeconds: z.number().int().positive(),
    assistedReviewSeconds: z.number().int().positive(),
})
    .strict();
export const M4AccessibilitySurfaceSchema = z.enum([
    "clinic_queue",
    "encounter_workspace",
    "care_bundle_review",
]);
export const M4AccessibilityEvidenceSchema = z
    .object({
    surface: M4AccessibilitySurfaceSchema,
    criticalViolationCount: z.number().int().nonnegative(),
    seriousViolationCount: z.number().int().nonnegative(),
    moderateViolationCount: z.number().int().nonnegative(),
    minorViolationCount: z.number().int().nonnegative(),
    keyboardPathPassed: z.boolean(),
    namesRolesStatesPassed: z.boolean(),
    liveRegionsPassed: z.boolean(),
    reducedMotionPassed: z.boolean(),
    reflowAt200PercentPassed: z.boolean(),
})
    .strict();
export const M4QualificationTargetsSchema = z
    .object({
    unchangedOrMinorEditRateMinimum: z.literal(0.8),
    administrativeTimeReductionMinimum: z.literal(0.5),
    maximumCriticalAccessibilityViolations: z.literal(0),
    maximumSeriousAccessibilityViolations: z.literal(0),
    maximumSafetyFalseNegatives: z.literal(0),
    maximumPositiveCaseBlockingFailures: z.literal(0),
})
    .strict();
export const M4OutcomeQualificationSuiteSchema = z
    .object({
    suiteId: IdSchema,
    tenantId: IdSchema,
    siteId: IdSchema,
    rolloutValidationSuite: RolloutValidationSuiteSchema,
    hardwareProfile: M4ClinicHardwareProfileSchema,
    targets: M4QualificationTargetsSchema,
    reviewObservations: z.array(M4ReviewObservationSchema).length(2),
    latencyEvidence: z.array(M4LatencyEvidenceSchema).length(7),
    accessibilityEvidence: z.array(M4AccessibilityEvidenceSchema).length(3),
    recordedBy: IdSchema,
    recordedAt: IsoDateTimeSchema,
    contentHash: Sha256Schema,
})
    .strict()
    .superRefine((suite, context) => {
    const reviewCases = new Set(suite.reviewObservations.map((item) => item.case));
    for (const expected of ["complete_fast_path", "complex_physician_deep_path"]) {
        if (!reviewCases.has(expected)) {
            context.addIssue({
                code: "custom",
                path: ["reviewObservations"],
                message: `Review evidence is missing ${expected}`,
            });
        }
    }
    if (reviewCases.size !== suite.reviewObservations.length) {
        context.addIssue({
            code: "custom",
            path: ["reviewObservations"],
            message: "Review evidence cases must be unique",
        });
    }
    const latencyMetrics = new Set(suite.latencyEvidence.map((item) => item.metric));
    for (const expected of M4LatencyMetricNameSchema.options) {
        if (!latencyMetrics.has(expected)) {
            context.addIssue({
                code: "custom",
                path: ["latencyEvidence"],
                message: `Latency evidence is missing ${expected}`,
            });
        }
    }
    if (latencyMetrics.size !== suite.latencyEvidence.length) {
        context.addIssue({
            code: "custom",
            path: ["latencyEvidence"],
            message: "Latency evidence metrics must be unique",
        });
    }
    const accessibilitySurfaces = new Set(suite.accessibilityEvidence.map((item) => item.surface));
    for (const expected of M4AccessibilitySurfaceSchema.options) {
        if (!accessibilitySurfaces.has(expected)) {
            context.addIssue({
                code: "custom",
                path: ["accessibilityEvidence"],
                message: `Accessibility evidence is missing ${expected}`,
            });
        }
    }
    if (accessibilitySurfaces.size !== suite.accessibilityEvidence.length) {
        context.addIssue({
            code: "custom",
            path: ["accessibilityEvidence"],
            message: "Accessibility evidence surfaces must be unique",
        });
    }
});
export const M4OutcomeQualificationReceiptSchema = z
    .object({
    suiteId: IdSchema,
    contentHash: Sha256Schema,
    unchangedOrMinorEditRate: z.number().min(0).max(1),
    administrativeTimeReduction: z.number().min(0).max(1),
    latencyPassed: z.boolean(),
    accessibilityPassed: z.boolean(),
    clinicalSafetyPassed: z.boolean(),
    passed: z.boolean(),
})
    .strict();
//# sourceMappingURL=m4-qualification.js.map